<template>
  <div class="p-6 border-b border-gray-200/60">
    <div class="mb-6">
      <h2
        class="text-gray-800 text-xl font-bold tracking-tight flex items-center gap-2"
        v-text="t('messages.my_portfolio')"
      />
      <div class="mt-1 h-0.5 w-12 bg-gradient-to-r from-primary to-indigo-400 rounded-full" />
    </div>

    <ul class="space-y-3">
      <li
        v-for="item in portfolioProgression"
        :key="item.id"
        class="group"
      >
        <div
          class="cursor-pointer flex bg-white/80 backdrop-blur-sm rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-200/40 min-h-[70px]"
          @click.prevent.stop="visitPortfolioTab(item.url)"
        >
          <div
            class="flex-shrink-0 cursor-pointer flex items-center justify-center w-16 text-white text-sm font-bold relative self-stretch"
            :class="[
              item.id === currentTab 
                ? 'bg-gradient-to-br from-primary via-indigo-600 to-purple-600 shadow-xl' 
                : 'bg-rose-500 hover:bg-rose-600 group-hover:shadow-lg'
            ]"
          >
            <!-- Dynamic icons based on portfolio type -->
            <component
              :is="getPortfolioIcon(item)"
              class="h-6 w-6 relative z-10 drop-shadow-sm"
            />
            <div 
              v-if="item.id === currentTab" 
              class="absolute inset-0 bg-white/20 animate-pulse"
            />
          </div>
          
          <div class="flex-1 flex items-center justify-between px-4 py-3 min-w-0">
            <div class="flex-1 min-w-0">
              <Link
                :href="item.url"
                class="block text-gray-900 font-semibold hover:text-primary transition-colors duration-200 truncate"
              >
                {{ item.label }}
              </Link>
              <div class="mt-1 flex items-center gap-2">
                <div class="flex-1 bg-gray-200/60 rounded-full h-1.5 overflow-hidden">
                  <div 
                    class="h-full bg-gradient-to-r from-primary to-indigo-500 rounded-full transition-all duration-500 ease-out"
                    :style="{ width: item.progression + '%' }"
                  />
                </div>
                <span
                  class="text-xs font-medium text-gray-600 tabular-nums"
                  v-text="item.progression + '%'"
                />
              </div>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import collect from 'collect.js';
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import { useMainStore } from '@/stores/main';
import { router } from '@inertiajs/vue3';
import { Link } from '@inertiajs/vue3';
import useBackendRouting from '@/composables/useBackendRouting';
import {
  UserIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  CogIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
  progression: {
    type: [Array, Object],
    required: true,
  },
});

const { t } = useI18n();

const {
  route,
} = useBackendRouting();

const store = useMainStore();

const portfolioNavTabs = [
  {
    id: 'positioning',
    label: t('messages.positioning'),
    url: route('broker_dashboard.my_portfolio.positioning.edit'),
    progression: null,
  },
  {
    id: 'organisation',
    label: t('messages.organisation'),
    url: route('broker_dashboard.my_portfolio.organisation.edit'),
    progression: null,
  },
  {
    id: 'conformity',
    label: t('messages.conformity'),
    url: route('broker_dashboard.my_portfolio.conformity.edit'),
    progression: null,
  },
  {
    id: 'strategy',
    label: t('messages.strategy'),
    url: route('broker_dashboard.my_portfolio.strategy.edit'),
    progression: null,
  },
];

if (store.broker.can_import_portfolio) {
  portfolioNavTabs.push({
    id: 'import',
    label: t('messages.import'),
    url: route('broker_dashboard.my_portfolio.import.edit'),
    progression: null,
  });
}

const portfolioProgression = computed(() => portfolioNavTabs.map((item) => {
  item.progression = collect(props.progression).get(item.id) || 0;
  return item;
}));

const currentTab = ref(null);

/**
 * Get the appropriate icon for each portfolio section.
 */
function getPortfolioIcon(item: any) {
  const iconMap = {
    'positioning': UserIcon,
    'organisation': CogIcon,
    'conformity': ClipboardDocumentCheckIcon,
    'strategy': DocumentTextIcon,
    'import': ClockIcon,
  };
  
  // Return mapped icon or fallback based on progression
  if (iconMap[item.id]) {
    return iconMap[item.id];
  }
  
  // Fallback based on progression percentage
  if (item.progression >= 100) {
    return CheckCircleIcon;
  } else if (item.progression > 0) {
    return ClockIcon;
  }
  
  return ExclamationCircleIcon;
}

/**
 * Set current tab ID.
 */
function setCurrentTab() {
  let tab = portfolioNavTabs.find((item) => item.url === window.location.href);
  currentTab.value = tab ? tab.id : null;
}

/**
 * Visit portfolio tab.
 */
function visitPortfolioTab(url: string) {
  router.visit(url, { preserveScroll: true });
}

setCurrentTab();
router.on('success', () => setCurrentTab());
</script>
